import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import axios from 'src/utils/axios';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import LevelBadge from 'src/components/BpoMutabakat/LevelBadge';
import { getLevelColor, getSeriesColors } from 'src/components/BpoMutabakat/colors';
import { formatDecimal, formatNumber, formatDate } from 'src/components/BpoMutabakat/format';
import { reportName } from 'src/components/BpoMutabakat/reports';

/** Ipsos kartındaki alt kırılım satırları — servis alan adları sabit. */
const SUB_SCORES = [
  ['approach', 'Yetkili yaklaşımı'],
  ['reachability', 'Yetkiliye ulaşım'],
  ['clarity', 'Bilgi anlaşılırlığı'],
  ['empathy', 'Empati'],
  ['politeness', 'Nezaket'],
  ['diction', 'Ses tonu ve diksiyon'],
  ['technicalHelp', 'Teknik destek faydası']
];

const QualityIpsos = ({ date }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);

  const [quality, setQuality] = useState(null);
  const [ipsos, setIpsos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;

    setLoading(true);
    setError('');

    Promise.allSettled([
      axios.get(`/GetBpoMutabakatQuality/${date}`),
      axios.get(`/GetBpoMutabakatIpsos/${date}`)
    ])
      .then(([kaliteSonuc, ipsosSonuc]) => {
        if (cancelled) return;
        setQuality(kaliteSonuc.status === 'fulfilled' ? kaliteSonuc.value?.data || null : null);
        setIpsos(
          ipsosSonuc.status === 'fulfilled' && Array.isArray(ipsosSonuc.value?.data)
            ? ipsosSonuc.value.data
            : []
        );
        if (kaliteSonuc.status === 'rejected' && ipsosSonuc.status === 'rejected') {
          setError(
            kaliteSonuc.reason?.response?.status === 404
              ? 'Kalite ve Ipsos servisleri henüz yayında değil.'
              : 'Veriler alınamadı. Lütfen tekrar deneyin.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date]);

  const distribution = quality?.scoreDistribution || [];
  const forms = quality?.forms || [];
  const summary = quality?.summary || null;

  const badColor = getLevelColor(theme, 'bad');
  const warnColor = getLevelColor(theme, 'warn');

  const scoreChart = useMemo(() => ({
    series: [{ name: 'Değerlendirme', data: distribution.map((d) => d.count) }],
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        background: 'transparent'
      },
      theme: { mode: theme.palette.mode },
      // Düşük puan kovaları dikkat isteyen bölge; ayrı renkle işaretleniyor
      // ki dağılımın sol kuyruğu gözden kaçmasın.
      colors: distribution.map((d, i) =>
        i === 0 ? badColor.text : i === 1 ? warnColor.text : colors[0]
      ),
      plotOptions: {
        bar: {
          borderRadius: 3,
          columnWidth: '55%',
          distributed: true
        }
      },
      legend: { show: false },
      dataLabels: { enabled: true, style: { fontSize: '11px' } },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
      xaxis: {
        categories: distribution.map((d) => d.bucket),
        title: { text: 'Değerlendirme puanı', style: { fontWeight: 400, fontSize: '11px' } },
        axisTicks: { show: false }
      },
      yaxis: { labels: { formatter: (v) => formatNumber(Math.round(v)) } },
      tooltip: {
        theme: theme.palette.mode,
        y: { formatter: (v) => `${formatNumber(v)} değerlendirme` }
      }
    }
  }), [distribution, theme, colors, badColor, warnColor]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <SectionCard
          title="Kalite değerlendirme puanları"
          subtitle={
            summary
              ? `${reportName(2)} · ${formatNumber(summary.evaluations)} değerlendirme`
              : reportName(2)
          }
        >
          {loading ? (
            <Skeleton variant="rounded" height={300} />
          ) : (
            <>
              <ReactApexChart
                options={scoreChart.options}
                series={scoreChart.series}
                type="bar"
                height={300}
              />
              {summary && (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    mt: 1
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      Ortalama puan
                    </Typography>
                    <LevelBadge value={summary.avgScore} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      En düşük
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(summary.minScore)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      En yüksek
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(summary.maxScore)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      Değerlendirilen agent
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(summary.agents)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 5 }}>
        <SectionCard title="Form tipi" subtitle={reportName(2)} padded={false}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Form</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell align="right">Adet</TableCell>
                  <TableCell align="center">Ort. puan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="caption" color="text.secondary">
                        {loading ? 'Yükleniyor…' : 'Bu gün kalite değerlendirmesi yok.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {forms.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell>{f.form}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {f.formType}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(f.count)}
                    </TableCell>
                    <TableCell align="center">
                      <LevelBadge value={f.avgScore} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <SectionCard
          title="Ipsos memnuniyet skorları"
          subtitle={
            ipsos.length
              ? `${reportName(18)} · dönem ${ipsos[0].period || ''}`
              : reportName(18)
          }
        >
          {loading ? (
            <Skeleton variant="rounded" height={220} />
          ) : ipsos.length === 0 ? (
            <InfoNote>
              Bu dönem için Ipsos skoru yazılmamış. Rapor aylık çalışır; ayın ilk günü
              işlendiğinde bir önceki ayın nihai skoru yazılır.
            </InfoNote>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))'
              }}
            >
              {ipsos.map((company) => (
                <Box
                  key={company.id}
                  sx={(t) => ({
                    border: `1px solid ${t.palette.divider}`,
                    borderRadius: 1,
                    p: 1.75,
                    backgroundColor:
                      t.palette.mode === 'dark' ? 'rgba(255,255,255,.03)' : t.palette.grey[50]
                  })}
                >
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                    {company.company}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5 }}>
                    {formatNumber(company.baseCount)} anket · {company.period}
                  </Typography>

                  {[
                    ['Hizmet memnuniyeti', company.satisfaction],
                    ['NPS', company.nps],
                    ['Tekrar satın alma', company.repurchase]
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ mb: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            fontVariantNumeric: 'tabular-nums'
                          }}
                        >
                          {formatDecimal(value)}
                        </Typography>
                      </Box>
                      <Box
                        sx={(t) => ({
                          height: 5,
                          borderRadius: 3,
                          mt: 0.4,
                          backgroundColor:
                            t.palette.mode === 'dark'
                              ? 'rgba(255,255,255,.08)'
                              : t.palette.grey[200],
                          overflow: 'hidden'
                        })}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.max(0, Math.min(100, Number(value) || 0))}%`,
                            borderRadius: 3,
                            backgroundColor: 'primary.main'
                          }}
                        />
                      </Box>
                    </Box>
                  ))}

                  <Box
                    sx={(t) => ({
                      borderTop: `1px solid ${t.palette.divider}`,
                      mt: 1.5,
                      pt: 1.25,
                      display: 'grid',
                      gap: 0.75
                    })}
                  >
                    {SUB_SCORES.filter(([key]) => company[key] !== null).map(([key, label]) => (
                      <Box key={key} sx={{ display: 'flex', fontSize: '0.75rem' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                          {label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.75rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {formatDecimal(company[key])}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <InfoNote sx={{ mt: 2 }}>
            {error ? (
              <b>{error}</b>
            ) : (
              <>
                <b>Ipsos aylık gelir, günlük değil.</b> Servis <code>yyyyMM</code> dönemiyle
                sorgulanır; iş işlediği günün ayını çeker ve satırları{' '}
                <code>VERI_TARIHI</code> = ayın ilk günü olarak yazar. Ay içinde her gece o ayın
                güncel skoru silinip yeniden yazılır. Ayın 1&apos;inde çalışan iş bir önceki günü
                işlediği için o ayın <b>nihai</b> skorunu yazar ve tabloda kalıcı olarak kalır.
                {ipsos.length > 0 && ipsos[0].periodStart && (
                  <> Gösterilen dönem: {formatDate(ipsos[0].periodStart)} itibarıyla.</>
                )}
              </>
            )}
          </InfoNote>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

QualityIpsos.propTypes = {
  date: PropTypes.string
};

export default QualityIpsos;
